import { CompanyOrgBuilder } from '@/components/features/Vacations/CompanyOrgBuilder';
import HeadingSmall from '@/components/layout/HeadingSmall';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Combobox } from '@/components/ui/shadcn/combobox';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { Switch } from '@/components/ui/shadcn/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/shadcn/tabs';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import SettingsLayout from '@/layouts/settings/layout';
import { type BreadcrumbItem } from '@/types';
import { Head, router, useForm } from '@inertiajs/react';
import { Plus, Trash2 } from 'lucide-react';
import { FormEventHandler } from 'react';
import { route } from 'ziggy-js';

const NO_CEO = '__none__';

interface UserRow {
    id: string;
    name: string;
    position: string | null;
    email_domain: string | null;
    legal_entity_id: string | null;
    legal_entity_manual: boolean;
    manager_id: string | null;
}

interface CompanyRow {
    id: string;
    name: string;
    ceo_id: string | null;
    ceo_name: string | null;
    email_domains: string[];
    director_name: string | null;
    city: string | null;
    logo_url: string | null;
    is_active: boolean;
}

interface LeaveTypeRow {
    id: string;
    slug: string;
    name: string;
    name_lt: string | null;
    pdf_label: string | null;
    is_active: boolean;
    sort_order: number;
}

interface VacationSettingsProps {
    users: UserRow[];
    companies: CompanyRow[];
    leaveTypes: LeaveTypeRow[];
    settings: {
        director_name: string;
        city: string;
        payout_timing_default: 'three_days_before' | 'with_salary';
        public_holidays: { date: string; name: string }[];
    };
}

export default function VacationSettings({ users, companies, leaveTypes, settings }: VacationSettingsProps) {
    const { t } = useTranslations();
    const breadcrumbs: BreadcrumbItem[] = [
        { title: t('vacations.nav'), href: '/vacations' },
        { title: t('vacations.settings.title'), href: '/settings/vacations' },
    ];

    // Step 1 — companies
    const newCompanyForm = useForm({ name: '', ceo_id: NO_CEO, email_domains: '', city: '' });
    const addCompany: FormEventHandler = (e) => {
        e.preventDefault();
        router.post(
            route('settings.vacations.entities.store'),
            {
                name: newCompanyForm.data.name,
                ceo_id: newCompanyForm.data.ceo_id === NO_CEO ? null : newCompanyForm.data.ceo_id,
                email_domains: newCompanyForm.data.email_domains,
                city: newCompanyForm.data.city,
            },
            { preserveScroll: true, onSuccess: () => newCompanyForm.reset() },
        );
    };
    const deleteCompany = (company: CompanyRow) => {
        if (!window.confirm(t('vacations.settings.entity_delete_confirm'))) return;
        router.delete(route('settings.vacations.entities.destroy', company.id), { preserveScroll: true });
    };

    // Step 3/4 — org chart
    const assignManager = (userId: string, managerId: string | null) => {
        router.put(route('settings.vacations.manager.update'), { user_id: userId, manager_id: managerId }, { preserveScroll: true });
    };
    const assignCompany = (userId: string, companyId: string | null) => {
        router.put(route('settings.vacations.user-company.update'), { user_id: userId, legal_entity_id: companyId }, { preserveScroll: true });
    };

    // Leave policy
    const settingsForm = useForm({
        director_name: settings.director_name ?? '',
        city: settings.city ?? 'Vilnius',
        payout_timing_default: settings.payout_timing_default ?? 'three_days_before',
        public_holidays: (settings.public_holidays ?? []).map((h) => `${h.date} ${h.name}`.trim()).join('\n'),
    });

    const prefillHolidays = () => router.post(route('settings.vacations.prefill-holidays'), {}, { preserveScroll: true });
    const saveSettings: FormEventHandler = (e) => {
        e.preventDefault();
        settingsForm.put(route('settings.vacations.update'), { preserveScroll: true });
    };

    const newTypeForm = useForm({ name: '', name_lt: '', pdf_label: '', is_active: true, sort_order: 0 });
    const addLeaveType: FormEventHandler = (e) => {
        e.preventDefault();
        newTypeForm.post(route('settings.vacations.leave-types.store'), { preserveScroll: true, onSuccess: () => newTypeForm.reset() });
    };
    const toggleLeaveType = (type: LeaveTypeRow) => {
        router.put(route('settings.vacations.leave-types.update', type.id), { is_active: !type.is_active }, { preserveScroll: true });
    };
    const deleteLeaveType = (type: LeaveTypeRow) => {
        if (!window.confirm(t('vacations.settings.leave_type_delete_confirm'))) return;
        router.delete(route('settings.vacations.leave-types.destroy', type.id), { preserveScroll: true });
    };

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={t('vacations.settings.title')} />
            <SettingsLayout>
                <div className="space-y-6">
                    <HeadingSmall title={t('vacations.settings.title')} description={t('vacations.subtitle')} />

                    {/* Step 1 & 2 — Companies */}
                    <Card>
                        <CardHeader>
                            <CardTitle>{t('vacations.settings.step_companies')}</CardTitle>
                            <CardDescription>{t('vacations.settings.companies_help')}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>{t('vacations.settings.entity_logo')}</TableHead>
                                        <TableHead>{t('vacations.settings.entity_name')}</TableHead>
                                        <TableHead>{t('vacations.settings.company_ceo')}</TableHead>
                                        <TableHead>{t('vacations.settings.company_domains')}</TableHead>
                                        <TableHead>{t('vacations.settings.entity_city')}</TableHead>
                                        <TableHead />
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {companies.map((company) => (
                                        <CompanyRow key={company.id} company={company} users={users} onDelete={() => deleteCompany(company)} />
                                    ))}
                                </TableBody>
                            </Table>

                            <form onSubmit={addCompany} className="flex flex-wrap items-end gap-3 border-t pt-4">
                                <div className="space-y-1">
                                    <Label htmlFor="new_company_name">{t('vacations.settings.entity_name')}</Label>
                                    <Input
                                        id="new_company_name"
                                        value={newCompanyForm.data.name}
                                        onChange={(e) => newCompanyForm.setData('name', e.target.value)}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label>{t('vacations.settings.company_ceo')}</Label>
                                    <Combobox
                                        className="w-48"
                                        options={users.map((user) => ({ value: user.id, label: user.name }))}
                                        value={newCompanyForm.data.ceo_id === NO_CEO ? '' : newCompanyForm.data.ceo_id}
                                        onValueChange={(v) => newCompanyForm.setData('ceo_id', v || NO_CEO)}
                                        placeholder={t('vacations.settings.company_ceo')}
                                        searchPlaceholder={t('vacations.settings.search')}
                                        emptyText={t('vacations.settings.no_results')}
                                        allowClear
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="new_company_domains">{t('vacations.settings.company_domains')}</Label>
                                    <Input
                                        id="new_company_domains"
                                        placeholder="firelita.lt"
                                        value={newCompanyForm.data.email_domains}
                                        onChange={(e) => newCompanyForm.setData('email_domains', e.target.value)}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="new_company_city">{t('vacations.settings.entity_city')}</Label>
                                    <Input
                                        id="new_company_city"
                                        value={newCompanyForm.data.city}
                                        onChange={(e) => newCompanyForm.setData('city', e.target.value)}
                                    />
                                </div>
                                <Button type="submit" disabled={newCompanyForm.processing || !newCompanyForm.data.name}>
                                    <Plus className="mr-1 h-4 w-4" />
                                    {t('vacations.settings.company_add')}
                                </Button>
                            </form>
                        </CardContent>
                    </Card>

                    {/* Step 3 & 4 — Org chart */}
                    <Card>
                        <CardHeader>
                            <CardTitle>{t('vacations.settings.step_org')}</CardTitle>
                            <CardDescription>{t('vacations.settings.org_chart_help')}</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Tabs defaultValue="org" className="w-full">
                                <TabsList>
                                    <TabsTrigger value="org">{t('vacations.settings.org_chart_tab')}</TabsTrigger>
                                    <TabsTrigger value="list">{t('vacations.settings.org_list_tab')}</TabsTrigger>
                                </TabsList>
                                <TabsContent value="org" className="mt-4">
                                    <CompanyOrgBuilder users={users} companies={companies} onAssignManager={assignManager} />
                                </TabsContent>
                                <TabsContent value="list" className="mt-4">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>{t('vacations.columns.employee')}</TableHead>
                                                <TableHead>{t('vacations.settings.company_column')}</TableHead>
                                                <TableHead>{t('vacations.settings.manager_column')}</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {users.map((user) => (
                                                <TableRow key={user.id}>
                                                    <TableCell>
                                                        <div className="font-medium">{user.name}</div>
                                                        {user.position && <div className="text-muted-foreground text-xs">{user.position}</div>}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Combobox
                                                            className="w-56"
                                                            options={companies.map((company) => ({ value: company.id, label: company.name }))}
                                                            value={user.legal_entity_id ?? ''}
                                                            onValueChange={(v) => assignCompany(user.id, v || null)}
                                                            placeholder={`${t('vacations.settings.auto_by_domain')}${user.email_domain ? ` · ${user.email_domain}` : ''}`}
                                                            searchPlaceholder={t('vacations.settings.search')}
                                                            emptyText={t('vacations.settings.no_results')}
                                                            allowClear
                                                            clearText={t('vacations.settings.auto_by_domain')}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Combobox
                                                            className="w-56"
                                                            options={users
                                                                .filter((candidate) => candidate.id !== user.id)
                                                                .map((candidate) => ({ value: candidate.id, label: candidate.name }))}
                                                            value={user.manager_id ?? ''}
                                                            onValueChange={(v) => assignManager(user.id, v || null)}
                                                            placeholder={t('vacations.settings.reports_to_ceo')}
                                                            searchPlaceholder={t('vacations.settings.search')}
                                                            emptyText={t('vacations.settings.no_results')}
                                                            allowClear
                                                            clearText={t('vacations.settings.reports_to_ceo')}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TabsContent>
                            </Tabs>
                        </CardContent>
                    </Card>

                    {/* Leave policy */}
                    <Card>
                        <CardHeader>
                            <CardTitle>{t('vacations.settings.step_policy')}</CardTitle>
                            <CardDescription>{t('vacations.settings.company_help')}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <form onSubmit={saveSettings} className="space-y-4">
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <div className="space-y-1">
                                        <Label htmlFor="director_name">{t('vacations.settings.director_name')}</Label>
                                        <Input
                                            id="director_name"
                                            value={settingsForm.data.director_name}
                                            onChange={(e) => settingsForm.setData('director_name', e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor="city">{t('vacations.settings.city')}</Label>
                                        <Input
                                            id="city"
                                            value={settingsForm.data.city}
                                            onChange={(e) => settingsForm.setData('city', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="payout_timing_default">{t('vacations.settings.payout_default')}</Label>
                                    <Select
                                        value={settingsForm.data.payout_timing_default}
                                        onValueChange={(v) => settingsForm.setData('payout_timing_default', v as 'three_days_before' | 'with_salary')}
                                    >
                                        <SelectTrigger id="payout_timing_default" className="w-full sm:w-96">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="three_days_before">{t('vacations.request.payout_three_days_before')}</SelectItem>
                                            <SelectItem value="with_salary">{t('vacations.request.payout_with_salary')}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1">
                                    <div className="flex items-center justify-between">
                                        <Label htmlFor="public_holidays">{t('vacations.settings.public_holidays')}</Label>
                                        <Button type="button" variant="outline" size="sm" onClick={prefillHolidays}>
                                            {t('vacations.settings.prefill_holidays')}
                                        </Button>
                                    </div>
                                    <Textarea
                                        id="public_holidays"
                                        rows={5}
                                        placeholder="2026-01-01 Naujieji metai&#10;2026-02-16 Lietuvos valstybės atkūrimo diena"
                                        value={settingsForm.data.public_holidays}
                                        onChange={(e) => settingsForm.setData('public_holidays', e.target.value)}
                                    />
                                </div>
                                <Button type="submit" disabled={settingsForm.processing}>
                                    {t('vacations.settings.save')}
                                </Button>
                            </form>

                            <div className="border-t pt-4">
                                <Label className="text-base font-semibold">{t('vacations.settings.leave_types_section')}</Label>
                                <Table className="mt-2">
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>{t('vacations.settings.leave_type_name')}</TableHead>
                                            <TableHead>{t('vacations.settings.leave_type_name_lt')}</TableHead>
                                            <TableHead>{t('vacations.settings.leave_type_pdf_label')}</TableHead>
                                            <TableHead>{t('vacations.settings.leave_type_active')}</TableHead>
                                            <TableHead />
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {leaveTypes.map((type) => (
                                            <TableRow key={type.id}>
                                                <TableCell>{type.name}</TableCell>
                                                <TableCell>{type.name_lt ?? '—'}</TableCell>
                                                <TableCell className="text-muted-foreground">{type.pdf_label ?? '—'}</TableCell>
                                                <TableCell>
                                                    <Switch checked={type.is_active} onCheckedChange={() => toggleLeaveType(type)} />
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Button variant="ghost" size="icon" onClick={() => deleteLeaveType(type)}>
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>

                                <form onSubmit={addLeaveType} className="mt-3 flex flex-wrap items-end gap-3">
                                    <div className="space-y-1">
                                        <Label htmlFor="new_name">{t('vacations.settings.leave_type_name')}</Label>
                                        <Input
                                            id="new_name"
                                            value={newTypeForm.data.name}
                                            onChange={(e) => newTypeForm.setData('name', e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor="new_name_lt">{t('vacations.settings.leave_type_name_lt')}</Label>
                                        <Input
                                            id="new_name_lt"
                                            value={newTypeForm.data.name_lt}
                                            onChange={(e) => newTypeForm.setData('name_lt', e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor="new_pdf_label">{t('vacations.settings.leave_type_pdf_label')}</Label>
                                        <Input
                                            id="new_pdf_label"
                                            value={newTypeForm.data.pdf_label}
                                            onChange={(e) => newTypeForm.setData('pdf_label', e.target.value)}
                                        />
                                    </div>
                                    <Button type="submit" disabled={newTypeForm.processing}>
                                        <Plus className="mr-1 h-4 w-4" />
                                        {t('vacations.settings.leave_type_add')}
                                    </Button>
                                </form>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </SettingsLayout>
        </AppLayout>
    );
}

function CompanyRow({ company, users, onDelete }: { company: CompanyRow; users: UserRow[]; onDelete: () => void }) {
    const { t } = useTranslations();

    const form = useForm({
        name: company.name,
        ceo_id: company.ceo_id ?? NO_CEO,
        email_domains: (company.email_domains ?? []).join(', '),
        city: company.city ?? '',
    });

    const save = (overrides: Record<string, unknown> = {}) =>
        router.put(
            route('settings.vacations.entities.update', company.id),
            {
                name: form.data.name,
                ceo_id: form.data.ceo_id === NO_CEO ? null : form.data.ceo_id,
                email_domains: form.data.email_domains,
                city: form.data.city,
                ...overrides,
            },
            { preserveScroll: true },
        );

    const logoForm = useForm<{ logo: File | null }>({ logo: null });
    const onLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        logoForm.setData('logo', file);
        logoForm.post(route('settings.vacations.entities.logo', company.id), { preserveScroll: true, forceFormData: true });
    };

    return (
        <TableRow>
            <TableCell>
                <div className="flex items-center gap-2">
                    {company.logo_url ? (
                        <img src={company.logo_url} alt={company.name} className="h-8 max-w-20 object-contain" />
                    ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                    )}
                    <label className="cursor-pointer">
                        <input type="file" accept="image/*" className="hidden" onChange={onLogoChange} />
                        <span className="text-primary text-xs underline">{t('vacations.settings.entity_logo_upload')}</span>
                    </label>
                </div>
            </TableCell>
            <TableCell>
                <Input value={form.data.name} onChange={(e) => form.setData('name', e.target.value)} onBlur={() => save()} className="w-36" />
            </TableCell>
            <TableCell>
                <Combobox
                    className="w-44"
                    options={users.map((user) => ({ value: user.id, label: user.name }))}
                    value={form.data.ceo_id === NO_CEO ? '' : form.data.ceo_id}
                    onValueChange={(v) => {
                        form.setData('ceo_id', v || NO_CEO);
                        save({ ceo_id: v || null });
                    }}
                    placeholder={t('vacations.settings.company_ceo')}
                    searchPlaceholder={t('vacations.settings.search')}
                    emptyText={t('vacations.settings.no_results')}
                    allowClear
                />
            </TableCell>
            <TableCell>
                <Input
                    value={form.data.email_domains}
                    onChange={(e) => form.setData('email_domains', e.target.value)}
                    onBlur={() => save()}
                    placeholder="firelita.lt"
                    className="w-40"
                />
            </TableCell>
            <TableCell>
                <Input value={form.data.city} onChange={(e) => form.setData('city', e.target.value)} onBlur={() => save()} className="w-28" />
            </TableCell>
            <TableCell className="text-right">
                <Button variant="ghost" size="icon" onClick={onDelete}>
                    <Trash2 className="h-4 w-4" />
                </Button>
            </TableCell>
        </TableRow>
    );
}
